import {
  createContext,
  createEffect,
  JSXElement,
  mergeProps,
  useContext
} from 'solid-js';
import { createStore } from 'solid-js/store';
import { createQuery } from '@tanstack/solid-query';
import {
  getPortalSessionDetails,
  getPortalSessionDetailsQuery,
  isApiError,
  retryAuthorizedRequests
} from '~/api';
import logger from '~/utils/logger';
import { HttpStatusCode } from 'axios';

type CurrentSessionData = {
  sessionExpiry?: string;
  accessTokenExpiry: string;
  organizationId?: string;
  subject: string;
  email?: string;
};

export type EnvAppDataContextValueType = {
  sessionData?: CurrentSessionData;
};

const EnvAppDataContext = createContext<EnvAppDataContextValueType>({});

type EnvAppDataContextProviderProps = {
  children: JSXElement;
};

export default function EnvAppDataContextProvider(
  props: EnvAppDataContextProviderProps
): JSXElement {
  const merged = mergeProps({}, props);

  const [contextValue, setContextValue] =
    createStore<EnvAppDataContextValueType>({});

  const sessionDetailsQuery = createQuery(() => ({
    queryKey: getPortalSessionDetailsQuery(),
    queryFn: async () => await getPortalSessionDetails(),
    retry: retryAuthorizedRequests
  }));

  createEffect(() => {
    if (sessionDetailsQuery.isSuccess && sessionDetailsQuery.data) {
      setContextValue('sessionData', sessionDetailsQuery.data);
    } else if (sessionDetailsQuery.isError && sessionDetailsQuery.error) {
      logger.error(
        `Failed to fetch session details `,
        sessionDetailsQuery.error
      );
      setContextValue('sessionData', {});
      isApiError(sessionDetailsQuery.error) &&
        sessionDetailsQuery.error.httpStatus === HttpStatusCode.BadRequest &&
        sessionDetailsQuery.refetch();
    }
  });

  return (
    <EnvAppDataContext.Provider value={contextValue}>
      {merged.children}
    </EnvAppDataContext.Provider>
  );
}

export const useEnvAppDataContext = () =>
  useContext(EnvAppDataContext) as EnvAppDataContextValueType;
